// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import * as Sentry from '@sentry/react';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import {
  RunMembershipRulesInput,
  SaveMembershipRulesInput,
  getMembershipRules,
  getMembershipScopeOptions,
  runMembershipRules,
  saveMembershipRules,
} from '@/membership/api/membership';
import {
  MembershipReportData,
  MembershipRules,
} from '@/membership/api/membership-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

// Validation rejections the save and run endpoints raise. They describe
// user-driven state the dialog shows inline, so they are not Sentry-worthy.
const HANDLED_API_STATUS_CODES = new Set<number>([
  5030, // Invalid definition
  5032, // Project has no start date, so it has no report window
  // A project running more than five years. The window comes from the project
  // rather than from a control on this page, so there is nothing to narrow and
  // the message is all the user gets.
  1703,
]);

/** A rejection classified for display. */
export class MembershipFailure extends Error {
  // What the caller shows beside the rules. Null means only support can fix
  // it, so the caller shows its snackbar rather than putting it in the form.
  readonly userMessage: string | null;

  constructor(userMessage: string | null) {
    super(userMessage ?? 'The membership request was rejected');
    this.name = 'MembershipFailure';
    this.userMessage = userMessage;
  }
}

interface SaveOptions {
  onSuccess?: () => void;
}

/** Classifies a rejection, reporting the unexpected ones to Sentry. */
function toMembershipFailure(error: unknown): MembershipFailure {
  if (!isAxiosError(error)) {
    Sentry.captureException(error);
    return new MembershipFailure(null);
  }
  const apiError = handleAPIError(error);
  if (HANDLED_API_STATUS_CODES.has(apiError.apiStatusCode)) {
    return new MembershipFailure(getUserFacingErrorMessage(apiError));
  }
  if (!apiError.sentToSentry) {
    Sentry.captureException(error);
  }
  return new MembershipFailure(null);
}

// The project's membership report, or the default definition when nobody has
// configured one.
export function useMembershipRules(projectKey: string) {
  return useQuery({
    queryKey: ['membership', 'rules', projectKey],
    queryFn: () => getMembershipRules(projectKey),
  });
}

// The SKUs, orders and locations a report may name. Each list is also the
// default for its facet, so it loads before the dialog can open.
export function useMembershipScopeOptions(
  projectKey: string,
  isEnabled: boolean
) {
  return useQuery({
    queryKey: ['membership', 'scope-options', projectKey],
    queryFn: () => getMembershipScopeOptions(projectKey),
    enabled: isEnabled,
  });
}

// The verdict is thrown rather than handed to a callback so it lands in the
// mutation's own error. That state belongs to the newest call, which is what
// makes a superseded failure drop itself and a success clear what came before,
// with no second copy of the error to fall out of step with.
export function useSaveMembershipRules(options?: SaveOptions) {
  const queryClient = useQueryClient();
  return useMutation<
    MembershipRules,
    MembershipFailure,
    SaveMembershipRulesInput
  >({
    mutationFn: (input) =>
      saveMembershipRules(input).catch((error: unknown) => {
        throw toMembershipFailure(error);
      }),
    meta: { isErrorReported: true },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['membership'] });
      options?.onSuccess?.();
    },
  });
}

// Run is a mutation rather than a query because it evaluates a definition the
// user is still editing, so it runs when asked and never on its own.
//
// There is no onSuccess here. A caller runs this against a draft and needs to
// know which draft answered, so it passes its own handler to mutate rather than
// registering one for every run.
export function useRunMembershipRules() {
  return useMutation<
    MembershipReportData,
    MembershipFailure,
    RunMembershipRulesInput
  >({
    mutationFn: (input) =>
      runMembershipRules(input).catch((error: unknown) => {
        throw toMembershipFailure(error);
      }),
    meta: { isErrorReported: true },
  });
}
